type AdministratorSessionState =
  | Readonly<{ state: "initializing" }>
  | Readonly<{ state: "anonymous"; reason: "session-ended" }>
  | Readonly<{
      state: "authenticated";
      user: Readonly<{ id: string }>;
      organizationId: string;
      permissions: readonly string[];
    }>;

export type WebSessionResponse =
  | Readonly<{
      state: "authenticated";
      accessToken: string;
      expiresAtEpochSeconds: number;
      principal: Readonly<{ id: string }>;
      organizationId: string;
      permissions: readonly string[];
    }>
  | Readonly<{ state: "temporarily-unavailable"; retryAfterSeconds: number }>
  | Readonly<{ state: "reauthentication-required" }>;

type WebSignOutResponse = Readonly<{ state: "signed-out"; logoutUrl: string }>;

export type WebSessionApi = Readonly<{
  read(forceRefresh: boolean): Promise<WebSessionResponse>;
  signOut(): Promise<WebSignOutResponse>;
}>;

type WebSessionBroadcastEvent = Readonly<{
  type: "session-refreshed" | "session-ended" | "signed-out";
}>;

export type WebSessionBroadcast = Readonly<{
  emit(event: WebSessionBroadcastEvent): void;
  listen(listener: (event: WebSessionBroadcastEvent) => void): () => void;
}>;

type ScheduleHandle = number | object;

type WebSessionControllerDependencies = Readonly<{
  api: WebSessionApi;
  broadcast: WebSessionBroadcast;
  nowEpochSeconds: () => number;
  schedule: (callback: () => void, delayMilliseconds: number) => ScheduleHandle;
  cancelSchedule: (handle: ScheduleHandle) => void;
}>;

export type WebSessionController = Readonly<{
  dispose(): void;
  fetchAccessToken(input: Readonly<{ forceRefreshToken: boolean }>): Promise<string | null>;
  getAuthorizationSnapshot(): Readonly<{ isRefreshing: boolean }>;
  getSnapshot(): AdministratorSessionState;
  initialize(): Promise<void>;
  onVisible(): Promise<void>;
  signOut(): Promise<WebSignOutResponse>;
  subscribe(listener: () => void): () => void;
}>;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseSessionResponse(value: unknown): WebSessionResponse {
  if (!isObject(value) || typeof value.state !== "string") throw new Error("Invalid response");
  if (value.state === "reauthentication-required") {
    if (Object.keys(value).length !== 1) throw new Error("Invalid response");
    return { state: value.state };
  }
  if (value.state === "temporarily-unavailable") {
    if (
      Object.keys(value).length !== 2 ||
      typeof value.retryAfterSeconds !== "number" ||
      !Number.isSafeInteger(value.retryAfterSeconds) ||
      value.retryAfterSeconds < 1 ||
      value.retryAfterSeconds > 30
    ) {
      throw new Error("Invalid response");
    }
    return { state: value.state, retryAfterSeconds: value.retryAfterSeconds };
  }
  if (
    value.state !== "authenticated" ||
    Object.keys(value).length !== 6 ||
    typeof value.organizationId !== "string" ||
    !/^org_[A-Za-z0-9]+$/.test(value.organizationId) ||
    !Array.isArray(value.permissions) ||
    value.permissions.length > 100 ||
    !value.permissions.every((item: unknown) => typeof item === "string" && item.length <= 128) ||
    typeof value.accessToken !== "string" ||
    value.accessToken.length < 16 ||
    value.accessToken.length > 16_384 ||
    typeof value.expiresAtEpochSeconds !== "number" ||
    !Number.isSafeInteger(value.expiresAtEpochSeconds) ||
    !isObject(value.principal) ||
    Object.keys(value.principal).length !== 1 ||
    typeof value.principal.id !== "string" ||
    value.principal.id.trim().length < 3 ||
    value.principal.id.length > 255
  ) {
    throw new Error("Invalid response");
  }
  return {
    state: value.state,
    accessToken: value.accessToken,
    expiresAtEpochSeconds: value.expiresAtEpochSeconds,
    principal: { id: value.principal.id },
    organizationId: value.organizationId,
    permissions: value.permissions as string[],
  };
}

function parseSignOutResponse(value: unknown): WebSignOutResponse {
  if (
    !isObject(value) ||
    Object.keys(value).length !== 2 ||
    value.state !== "signed-out" ||
    typeof value.logoutUrl !== "string" ||
    value.logoutUrl.length > 4_096
  ) {
    throw new Error("Invalid response");
  }
  const logoutUrl = new URL(value.logoutUrl);
  const browserOrigin =
    typeof globalThis.location === "undefined" ? null : globalThis.location.origin;
  if (
    logoutUrl.protocol !== "https:" ||
    (logoutUrl.hostname !== "api.workos.com" && logoutUrl.origin !== browserOrigin)
  ) {
    throw new Error("Invalid response");
  }
  return { state: value.state, logoutUrl: logoutUrl.href };
}

async function boundedJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length > 20_000) throw new Error("Invalid response");
  return JSON.parse(text) as unknown;
}

export function createFetchWebSessionApi(
  request: typeof fetch = globalThis.fetch.bind(globalThis),
): WebSessionApi {
  const headers = {
    "content-type": "application/json",
    "x-remindertracker-request": "administrator-auth-v1",
  };
  return {
    async read(forceRefresh) {
      let response: Response;
      try {
        response = await request("/auth/session", {
          body: JSON.stringify({ forceRefresh }),
          credentials: "same-origin",
          headers,
          method: "POST",
          redirect: "error",
          signal: AbortSignal.timeout(10_000),
        });
      } catch {
        return { state: "temporarily-unavailable", retryAfterSeconds: 3 };
      }
      try {
        const body = parseSessionResponse(await boundedJson(response));
        if (response.status === 200 && body.state === "authenticated") return body;
        if (response.status === 401 && body.state === "reauthentication-required") return body;
        if (response.status === 503 && body.state === "temporarily-unavailable") return body;
      } catch {
        // Untrusted or malformed responses never become browser authority.
      }
      return { state: "temporarily-unavailable", retryAfterSeconds: 3 };
    },
    async signOut() {
      const response = await request("/auth/sign-out", {
        body: "{}",
        credentials: "same-origin",
        headers,
        method: "POST",
        redirect: "error",
      });
      if (response.status !== 200) throw new Error("Sign-out unavailable");
      return parseSignOutResponse(await boundedJson(response));
    },
  };
}

export function createBrowserSessionBroadcast(): WebSessionBroadcast {
  let channel: BroadcastChannel | null = null;
  const getChannel = () => (channel ??= new BroadcastChannel("rt.administrator.session.v1"));
  return {
    emit(event) {
      getChannel().postMessage(event);
    },
    listen(listener) {
      const handler = (event: MessageEvent<unknown>) => {
        if (
          isObject(event.data) &&
          Object.keys(event.data).length === 1 &&
          (event.data.type === "session-refreshed" ||
            event.data.type === "session-ended" ||
            event.data.type === "signed-out")
        ) {
          listener({ type: event.data.type });
        }
      };
      getChannel().addEventListener("message", handler);
      return () => {
        channel?.removeEventListener("message", handler);
        channel?.close();
        channel = null;
      };
    },
  };
}

export function createWebSessionController(
  dependencies: WebSessionControllerDependencies,
): WebSessionController {
  let snapshot: AdministratorSessionState = { state: "initializing" };
  let accessToken: string | null = null;
  let accessTokenExpiry = 0;
  let authorizationSnapshot: Readonly<{ isRefreshing: boolean }> = { isRefreshing: false };
  let scheduledRefresh: ScheduleHandle | null = null;
  let stopped = false;
  let signingOut = false;
  let initialized = false;
  let latestRequestRevision = 0;
  let stopListening: (() => void) | null = null;
  let terminalProbePromise: Promise<string | null> | null = null;
  const listeners = new Set<() => void>();

  const publish = (
    next: AdministratorSessionState,
    nextAuthorizationSnapshot = authorizationSnapshot,
  ) => {
    snapshot = next;
    authorizationSnapshot = nextAuthorizationSnapshot;
    for (const listener of listeners) listener();
  };
  const cancelRefresh = () => {
    if (scheduledRefresh !== null) dependencies.cancelSchedule(scheduledRefresh);
    scheduledRefresh = null;
  };
  const clear = (broadcastEvent: boolean) => {
    cancelRefresh();
    accessToken = null;
    accessTokenExpiry = 0;
    publish({ state: "anonymous", reason: "session-ended" }, { isRefreshing: false });
    if (broadcastEvent) dependencies.broadcast.emit({ type: "session-ended" });
  };
  const suspend = () => {
    cancelRefresh();
    accessToken = null;
    accessTokenExpiry = 0;
    publish(snapshot.state === "authenticated" ? snapshot : { state: "initializing" }, {
      isRefreshing: true,
    });
  };
  const scheduleRefresh = () => {
    cancelRefresh();
    if (stopped || accessToken === null) return;
    const delayMilliseconds = Math.max(
      1_000,
      (accessTokenExpiry - dependencies.nowEpochSeconds() - 60) * 1_000,
    );
    scheduledRefresh = dependencies.schedule(() => {
      void read(true, true);
    }, delayMilliseconds);
  };
  const isInactive = (): boolean => stopped || signingOut;
  const read = async (
    forceRefresh: boolean,
    announceRefresh: boolean,
    terminalProbeAllowed = true,
    announceTerminal = true,
  ): Promise<string | null> => {
    if (signingOut) return null;
    if (terminalProbePromise !== null) return await terminalProbePromise;
    const requestRevision = (latestRequestRevision += 1);
    const response = await dependencies.api.read(forceRefresh);
    if (isInactive()) return null;
    if (requestRevision !== latestRequestRevision) {
      return accessToken !== null && accessTokenExpiry > dependencies.nowEpochSeconds() + 5
        ? accessToken
        : null;
    }
    if (response.state === "authenticated") {
      const principalChanged =
        snapshot.state === "authenticated" && snapshot.user.id !== response.principal.id;
      accessToken = response.accessToken;
      accessTokenExpiry = response.expiresAtEpochSeconds;
      publish(
        {
          state: "authenticated",
          organizationId: response.organizationId,
          permissions: response.permissions,
          user: { id: response.principal.id },
        },
        { isRefreshing: principalChanged },
      );
      scheduleRefresh();
      if (announceRefresh) dependencies.broadcast.emit({ type: "session-refreshed" });
      return response.accessToken;
    }
    if (response.state === "reauthentication-required") {
      // A response can be terminal for an old seal while another concurrent
      // response has already rotated the HttpOnly cookie. Probe the browser's
      // current cookie once before settling the tab as anonymous.
      if (terminalProbeAllowed) {
        suspend();
        const probe = read(false, false, false, announceTerminal);
        terminalProbePromise = probe;
        try {
          return await probe;
        } finally {
          if (terminalProbePromise === probe) terminalProbePromise = null;
        }
      }
      clear(announceTerminal);
      return null;
    }
    const stillUsable =
      accessToken !== null && accessTokenExpiry > dependencies.nowEpochSeconds() + 5;
    if (!stillUsable && snapshot.state === "authenticated") {
      publish(snapshot, { isRefreshing: true });
    }
    dependencies.schedule(() => {
      if (!stopped) void read(false, false, terminalProbeAllowed, announceTerminal);
    }, response.retryAfterSeconds * 1_000);
    return !forceRefresh && stillUsable ? accessToken : null;
  };

  const onBroadcast = (event: WebSessionBroadcastEvent) => {
    if (event.type === "signed-out") {
      // Invalidate a read already in flight in this tab. Its HTTP response may
      // still arrive, but it cannot republish browser authority after another
      // tab has completed sign-out.
      latestRequestRevision += 1;
      clear(false);
      return;
    }
    // A terminal response can be stale relative to another tab's successful
    // cookie rotation. Re-probe the HttpOnly cookie instead of trusting the
    // broadcast as session authority.
    void read(false, false, false, false);
  };

  return {
    dispose() {
      stopped = true;
      cancelRefresh();
      stopListening?.();
      stopListening = null;
      listeners.clear();
    },
    async fetchAccessToken({ forceRefreshToken }) {
      if (!forceRefreshToken && terminalProbePromise !== null) {
        return await terminalProbePromise;
      }
      const usable =
        accessToken !== null && accessTokenExpiry > dependencies.nowEpochSeconds() + 15;
      if (!forceRefreshToken && usable) {
        if (authorizationSnapshot.isRefreshing) {
          publish(snapshot, { isRefreshing: false });
        }
        return accessToken;
      }
      return await read(forceRefreshToken, forceRefreshToken);
    },
    getAuthorizationSnapshot() {
      return authorizationSnapshot;
    },
    getSnapshot() {
      return snapshot;
    },
    async initialize() {
      if (initialized) return;
      initialized = true;
      stopListening = dependencies.broadcast.listen(onBroadcast);
      await read(false, false);
    },
    async onVisible() {
      if (snapshot.state === "authenticated") await read(true, true);
    },
    async signOut() {
      signingOut = true;
      latestRequestRevision += 1;
      clear(false);
      try {
        const response = await dependencies.api.signOut();
        dependencies.broadcast.emit({ type: "signed-out" });
        return response;
      } catch (error) {
        signingOut = false;
        await read(false, false);
        throw error;
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
